import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Alert,
  Popover,
  PopoverBody,
  Input,
  Label,
  FormGroup,
  Carousel,
  CarouselItem,
  CarouselControl,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
} from "reactstrap";
import { AppContext } from "../context";
import { isInternalAccess } from "../common/service";
import catalogService from "./service";
import { createCollectionProduct } from "../store/collectionsService";
import {
  createStoreProject,
  createStoreProjectItem,
  addStoreProductToCartProject,
  getStoreProjects,
  sortStoreProjectsEarliestFirst,
  sortedStoreProjectsFromApiPayload,
  sortedStoreProjectsForBuyerAccount,
  sortStoreProjectsForBuyerAccount,
  normalizeStoreUserId,
  firstManageableProjectIdFromList,
  projectCanManageItems,
  projectRowNeedsManageFlagFromProjectsApi,
  mergeBuyerAccountProjectLists,
  mergeStoreProjectRowsById,
  filterOwnStoreDraftQuotesForAdd,
  MY_FIRST_QUOTE_PROJECT_NAME,
  shouldPromptStoreProjectName,
} from "../store/service";
import StoreProjectNameModal from "../store/components/StoreProjectNameModal";
import { normalizeStoreProjectFolderPath } from "../store/projectFolderPath";
import { emptyConfig } from "./productDetailConfig";
import PriceOrLogin from "../store/components/PriceOrLogin";
import { StoreBuyerQuotePickerItems } from "../store/components/StoreBuyerQuotePickerItems";
import { StoreQuoteFolderPickerModal } from "../store/components/StoreQuoteFolderPickerModal";
import StoreDeliveryChargesInfo from "../store/components/StoreDeliveryChargesInfo";
import RangeComparisonModal from "../store/components/RangeComparisonModal";
import SamplePreviewModal from "../store/components/SamplePreviewModal";
import SizingGrid from "../store/components/SizingGrid";
import WarrantyTermsModal from "../store/components/WarrantyTermsModal";
import ProductPriceBreakdown from "./ProductPriceBreakdown";
import { STORE_MISSING_IMAGE_URL } from "../constants";
import { Link, useNavigate } from "react-router-dom";
import {
  useStableNewVariantAxes,
  pickAxisProductId,
  pickVariantSizeProductIds,
  resolveVariantSizeAxes,
  toNullableNumber,
  StoreProductVariantDimensionPickers,
} from "../store/variantAxes";
import { getDeliveryTextForSelectedVariant } from "../store/pages/storeProjectDetail/cartUtils";
import { trackZbAddToCart, trackZbProductView } from "../common/analytics";
import { isZerobuildDomain } from "../utils";
import "./ProductDetailModal.css";
import "../store/products.css";

/** Do not assume API/data contracts. Returns first string URL found from obj using candidate keys; keys/config should come from API contract or app config. */
function getFirstImageUrl(obj, candidateKeys = []) {
  if (!obj || typeof obj !== "object") return null;

  for (const key of candidateKeys) {
    const v = obj[key];
    if (typeof v === "string" && v.trim()) return v.trim();
    if (Array.isArray(v) && v.length > 0) {
      const first = v[0];
      if (typeof first === "string" && first.trim()) return first.trim();
      if (first && typeof first.url === "string" && first.url.trim())
        return first.url.trim();
    }
  }

  return null;
}

/** Returns an array of product image URLs (product.images only, not variant/feature images). Falls back to single image if no images array. */
function getProductImageUrls(product) {
  if (!product || typeof product !== "object") return [];

  const raw = product.images;

  if (Array.isArray(raw) && raw.length > 0) {
    return raw
      .map((item) =>
        typeof item === "string" && item.trim() ? item.trim() : item?.url
      )
      .filter(Boolean);
  }

  const single = getFirstImageUrl(product, ["image", "main_image"]);

  return single ? [single] : [];
}

function getProductDetailImageOverlayText(imageIndex) {
  if (imageIndex === 0) {
    return "Height excludes plinth/legs";
  }
  if (imageIndex === 1) {
    return "Shutters shown for representation only. Not included.";
  }
  return "Reference photos of a Prime-Minifix product";
}

function resolvePublicRelativeUrl(path) {
  const raw = String(path || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `/${raw.replace(/^\/+/, "")}`;
}

function normalizeHighlightFolderLinks(rawFolders) {
  if (!Array.isArray(rawFolders)) return [];

  const seen = new Set();
  const out = [];
  rawFolders.forEach((entry) => {
    const row =
      entry && typeof entry === "object"
        ? entry
        : { name: String(entry || "") };
    const labelRaw = row.name ?? row.label ?? row.title ?? "";
    const label = String(labelRaw).trim();
    if (!label) return;

    const hrefRaw =
      row.url ??
      row.href ??
      row.path ??
      (row.slug
        ? `/products?folder=${encodeURIComponent(String(row.slug))}`
        : null);
    const href =
      typeof hrefRaw === "string" && hrefRaw.trim() ? hrefRaw.trim() : "";
    const imageUrlRaw = row.image_url ?? row.imageUrl ?? row.image ?? null;
    const imageUrl =
      typeof imageUrlRaw === "string" && imageUrlRaw.trim()
        ? imageUrlRaw.trim()
        : null;
    const dedupeKey = `${label}::${href}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    out.push({ label, href, imageUrl });
  });

  return out;
}

function ProductDetailModalNewShapeVariants({
  product,
  disabled,
  onPickProductId,
}) {
  const axes = useStableNewVariantAxes(product);
  const listProductId = toNullableNumber(product?.id);
  const rangeOpts = axes?.range ?? [];
  const rangePid = pickAxisProductId(rangeOpts, listProductId);
  const [isRangeHelpOpen, setIsRangeHelpOpen] = useState(false);

  const handlePid = (nextPid) => {
    const nid = toNullableNumber(nextPid);
    if (nid == null || nid === listProductId) return;
    onPickProductId(nid);
  };

  return (
    <div
      className="store-products-row__controls"
      style={{ padding: "8px 16px 8px 16px" }}
    >
      <StoreProductVariantDimensionPickers
        axes={axes}
        listProductId={listProductId}
        onPickProductId={handlePid}
        disabled={disabled}
        keyPrefix="modal"
      />
      {rangeOpts.length > 0 ? (
        <div className="mt-2">
          <div
            className="store-products-row__range-pills"
            role="group"
            aria-label="Range"
          >
            {rangeOpts.map((o) => {
              const pid = toNullableNumber(o?.product_id);
              const active =
                pid != null && rangePid != null && pid === rangePid;
              return (
                <button
                  key={`r-${pid}`}
                  type="button"
                  className={
                    active
                      ? "store-products-row__range-pill store-products-row__range-pill--active"
                      : "store-products-row__range-pill"
                  }
                  disabled={disabled}
                  onClick={() => pid != null && handlePid(pid)}
                >
                  {String(o.label ?? "").trim()}
                </button>
              );
            })}
            <button
              type="button"
              className="btn btn-link p-0 text-secondary text-decoration-none store-products-row__range-help"
              aria-label="Open range comparison image"
              onClick={() => setIsRangeHelpOpen(true)}
            >
              <i
                className="fa-regular fa-circle-question fs-6"
                aria-hidden="true"
              />
            </button>
            <RangeComparisonModal
              isOpen={isRangeHelpOpen}
              toggle={() => setIsRangeHelpOpen(false)}
              warrantyByRange={product?.warranty_by_range}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

const CORE_MATERIAL_DISPLAY = {
  r1_bwp: "Eco BWP Plywood",
  r2_bwp: "Prime BWP Plywood",
  r3_bwp: "Gold BWP Plywood",
  r1_mr: "Eco MR Plywood",
  r2_mr: "Prime MR Plywood",
  r3_mr: "Gold MR Plywood",
};

const COLOR_DISPLAY = {
  off_white_backer: { name: "Half White", finish: "half_white" },
  beige_fabric_backer: { name: "Beige Fabric", finish: "beige_fabric" },
};

const HARDWARE_DISPLAY_NAMES = {
  "4x16": "Screw 4×16",
  "4x20": "Screw 4×20",
  "4x30": "Screw 4×30",
  "4x38": "Screw 4×38",
  "4x50": "Screw 4×50",
  l_clamp: "L-Clamp",
  shelf_pin: "Shelf Pin",
};

function normalizePackingRows(contents, packingList) {
  if (!contents || typeof contents !== "object") return [];

  const rowsByBucket = {
    panels: [],
    hardwares: [],
    accessories: [],
    fixtures: [],
    others: [],
  };

  const normalizeItemLabel = (item) =>
    String(HARDWARE_DISPLAY_NAMES[item] ?? item ?? "")
      .replace(/_/g, " ")
      .trim();

  const readCount = (value) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return value.qty;
    }
    return value;
  };

  const readSku = (value) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const sku = value.sku;
      if (sku == null) return null;
      const trimmed = String(sku).trim();
      return trimmed || null;
    }
    return null;
  };

  const toBucket = (category) => {
    const key = String(category ?? "")
      .trim()
      .toLowerCase();
    if (key === "panels") return "panels";
    if (key === "hardwares") return "hardwares";
    if (key === "accessories") return "accessories";
    if (key === "fixtures") return "fixtures";
    return "others";
  };

  const pushRow = (category, item, value, itemDetail = null) => {
    const count = readCount(value);
    if (count == null || count === "") return;
    rowsByBucket[toBucket(category)].push({
      category,
      item: normalizeItemLabel(item),
      itemDetail:
        itemDetail != null && String(itemDetail).trim() !== ""
          ? String(itemDetail).trim()
          : null,
      sku: readSku(value),
      count: `${count} nos.`,
    });
  };

  pushRow(
    "panels",
    "Panels",
    packingList.reduce((total, list) => total + list["parts"].length, 0),
    "(18mm for carcass, 8mm for backsheet)"
  );

  Object.entries(contents.fixtures ?? {}).forEach(([item, count]) =>
    pushRow("fixtures", item, count)
  );

  Object.entries(contents.hardwares ?? {}).forEach(([item, count]) =>
    pushRow("hardwares", item, count)
  );

  Object.entries(contents.accessories ?? {}).forEach(([item, count]) =>
    pushRow("accessories", item, count)
  );

  return [
    ...rowsByBucket.panels,
    ...rowsByBucket.accessories,
    ...rowsByBucket.hardwares,
    ...rowsByBucket.fixtures,
    ...rowsByBucket.others,
  ];
}

function normalizePackingListRows(packingList) {
  if (!Array.isArray(packingList)) return [];

  return packingList.map((row) => ({
    label: row.label,
    panels: row.parts.map((p) => p.name).join(", "),
  }));
}

function normalizeShutterPanels(raw) {
  if (!Array.isArray(raw)) return [];

  return raw;
}

const PRODUCT_CUTLIST_COLUMNS = [
  { key: "description", label: "Description" },
  { key: "fb_name", label: "FB Name" },
  { key: "thickness", label: "Thickness" },
  { key: "fb_length", label: "FB Length" },
  { key: "fb_width", label: "FB Width" },
  { key: "quantity", label: "Quantity" },
  { key: "texture_direction", label: "Texture Direction" },
  { key: "left_edge_band_amount", label: "Left Edge band Amount" },
  { key: "right_edge_band_amount", label: "Right Edge Band Amount" },
  { key: "top_edge_band_amount", label: "Top Edge Band Amount" },
  { key: "bottom_edge_band_amount", label: "Bottom Edge Band Amount" },
  { key: "sku", label: "SKU" },
  { key: "panel_name", label: "Panel Name" },
  { key: "room_name", label: "Room Name" },
  { key: "remark", label: "Remark" },
  { key: "area_name", label: "Area Name" },
  { key: "assembly_label", label: "Assembly Label" },
];

const PRODUCT_SHUTTER_CUTLIST_COLUMNS = [
  { key: "area", label: "Area" },
  { key: "cabinet", label: "Cabinet" },
  { key: "label", label: "Label" },
  { key: "length_1", label: "Length (incl edge)" },
  { key: "length_2", label: "Width (incl edge)" },
];

function normalizeCutlistRows(raw) {
  if (!Array.isArray(raw)) return [];

  return raw;
}

function normalizeShutterCutlistRows(raw) {
  if (!Array.isArray(raw)) return [];

  return raw;
}

/**
 * Single product-detail modal: catalog flow (variant selection + Add to Project).
 * Note: this modal intentionally does not include cart "update item" mode logic.
 */
const ProductDetailModal = ({
  product,
  initialConfig,
  onClose,
  productId,
  isOpen: isOpenProp,
  getProduct,
  onAddToCart,
  onProductIdChange,
  nonCartInitialAdded,
  nonCartInitialHasProjects,
  nonCartInitialFolderPath,
}) => {
  const { currentUser, openAuthModal } = React.useContext(AppContext) || {};
  const [resolvedProduct, setResolvedProduct] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [config, setConfig] = useState(() => initialConfig || emptyConfig());
  const [selectedProductId, setSelectedProductId] = useState(() =>
    product ? String(product.id ?? product.product_id ?? "") : ""
  );
  const [addError, setAddError] = useState(null);
  const [addedOnce, setAddedOnce] = useState(false);
  const [addedProjectId, setAddedProjectId] = useState(null);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );
  const [pricePopoverOpen, setPricePopoverOpen] = useState(false);
  const [isVariantLoading, setIsVariantLoading] = useState(false);
  const [projectsList, setProjectsList] = useState([]);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [createProjectNameModal, setCreateProjectNameModal] = useState({
    isOpen: false,
    draftName: "",
    busy: false,
    error: null,
  });
  const [folderPickerModal, setFolderPickerModal] = useState({
    isOpen: false,
    projectId: null,
    projectName: "",
  });

  useEffect(() => {
    if (createProjectNameModal.isOpen || folderPickerModal.isOpen) {
      setProjectDropdownOpen(false);
    }
  }, [createProjectNameModal.isOpen, folderPickerModal.isOpen]);
  const [nonCartAddedCount, setNonCartAddedCount] = useState(0);
  const [nonCartCartBusy, setNonCartCartBusy] = useState(false);
  const variantRequestIdRef = useRef(null);
  const [frozenDisplayProduct, setFrozenDisplayProduct] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [carouselAnimating, setCarouselAnimating] = useState(false);
  const touchStartXRef = useRef(null);
  const edgeSwipeStartXRef = useRef(null);
  const edgeSwipeStartYRef = useRef(null);
  const [isAddToCollectionModalOpen, setIsAddToCollectionModalOpen] =
    useState(false);
  const [selectedCollectionId, setSelectedCollectionId] = useState("");
  const [addToCollectionSubmitting, setAddToCollectionSubmitting] =
    useState(false);
  const [addToCollectionError, setAddToCollectionError] = useState(null);
  const [samplePreview, setSamplePreview] = useState({
    isOpen: false,
    name: "",
    url: "",
  });
  const [isWarrantyTermsOpen, setIsWarrantyTermsOpen] = useState(false);
  const [isPackingListModalOpen, setIsPackingListModalOpen] = useState(false);
  const [isCutlistModalOpen, setIsCutlistModalOpen] = useState(false);
  const [isShuttersModalOpen, setIsShuttersModalOpen] = useState(false);
  const [isSizingGridOpen, setIsSizingGridOpen] = useState(false);
  const initialFetchRef = useRef({ productId: null, promise: null });
  const skipRefetchAfterMergeRef = useRef(false);
  const searchLogoPath = resolvePublicRelativeUrl("store/zb_icon.png");
  const navigate = useNavigate();

  const isLoggedIn = Boolean(currentUser?.mobile);
  /** Project picker only for logged-in non–cart-mode accounts. Guests = cart add flow. */
  const isProjectPickerUser = isLoggedIn && currentUser?.is_cart !== true;
  const isCustomerUser = isLoggedIn && currentUser?.customer === true;
  const isDealerByType =
    typeof currentUser?.type === "string" &&
    currentUser.type.toLowerCase().includes("dealer");
  const isDealerUser =
    isLoggedIn && (currentUser?.dealer === true || isDealerByType);
  const isAdminUser = isLoggedIn && currentUser?.admin === true;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const useZbColumnModal = isZerobuildDomain() && !isMobile;

  const displayProduct = product ?? resolvedProduct;
  const productArchived = displayProduct?.archived === true;
  const addToQuoteDisabled =
    loadingProduct || isVariantLoading || productArchived;
  const addToQuoteTooltipId = "product-detail-add-to-quote-archived";
  const isOpenDerived = Boolean(displayProduct);
  const isOpen = isOpenProp !== undefined ? isOpenProp : isOpenDerived;
  const viewedProductIdRef = useRef(null);
  const initialProductId = displayProduct
    ? String(displayProduct.id ?? displayProduct.product_id ?? "")
    : "";

  const initialConfigRef = useMemo(
    () => initialConfig ?? displayProduct?.default_config ?? emptyConfig(),
    [initialConfig, displayProduct]
  );

  // Initial load only: fetch product without config when we have no product data yet. Do not run on variant change (refetch effect handles that with config).
  useEffect(() => {
    if (!productId || product != null) {
      setResolvedProduct(null);
      setLoadError(null);
      initialFetchRef.current = { productId: null, promise: null };
      return;
    }
    if (!isOpenProp) return;
    const id = String(productId);
    const currentResolvedId =
      resolvedProduct != null
        ? String(resolvedProduct.id ?? resolvedProduct.product_id ?? "")
        : "";
    const currentResolvedCode =
      resolvedProduct != null ? String(resolvedProduct.code ?? "").trim() : "";
    if (
      resolvedProduct != null &&
      currentResolvedCode &&
      currentResolvedCode === id
    ) {
      return;
    }
    if (resolvedProduct != null && currentResolvedId === id) return;
    const cached = initialFetchRef.current;
    if (cached.promise && cached.productId === id) {
      setLoadingProduct(true);
      cached.promise
        .then((res) => {
          const body = res?.data ?? res;
          const data = body?.data ?? body;
          let nextResolvedProduct = null;
          if (data && (data.id || data.product_id)) nextResolvedProduct = data;
          setResolvedProduct(nextResolvedProduct);
          if (!data || (!data.id && !data.product_id))
            setLoadError("Product not found");
        })
        .catch((err) => setLoadError(err?.message || "Failed to load product"))
        .finally(() => setLoadingProduct(false));
      return;
    }

    let cancelled = false;
    setLoadError(null);
    setLoadingProduct(true);

    const fetchProduct = getProduct
      ? getProduct(productId)
      : catalogService.getCatalogProduct(productId);

    const promise = Promise.resolve(fetchProduct)
      .then((res) => {
        if (cancelled) return res;
        const body = res?.data ?? res;
        const data = body?.data ?? body;
        let nextResolvedProduct = null;
        if (data && (data.id || data.product_id)) nextResolvedProduct = data;
        setResolvedProduct(nextResolvedProduct);
        if (!data || (!data.id && !data.product_id))
          setLoadError("Product not found");
        return res;
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err?.message || "Failed to load product");
        throw err;
      })
      .finally(() => {
        if (!cancelled) setLoadingProduct(false);
        if (initialFetchRef.current.productId === id)
          initialFetchRef.current = { productId: null, promise: null };
      });

    initialFetchRef.current = { productId: id, promise };

    return () => {
      cancelled = true;
    };
  }, [
    productId,
    isOpenProp,
    product,
    getProduct,
    resolvedProduct,
    selectedProductId,
  ]);

  // Sync config and selection when modal opens or product changes. Skip when the only change was our own merge (variant fetch completed) so we don't overwrite state and retrigger refetch.
  useEffect(() => {
    if (!isOpen) return;

    if (skipRefetchAfterMergeRef.current) {
      skipRefetchAfterMergeRef.current = false;
      return;
    }

    setConfig(initialConfigRef);
    setSelectedProductId(initialProductId);
    setAddError(null);
    setAddedOnce(false);
    setAddedProjectId(null);
    setIsVariantLoading(false);
    setFrozenDisplayProduct(null);
  }, [isOpen, initialProductId, initialConfigRef]);

  const displayProductId = displayProduct?.id ?? displayProduct?.product_id;

  useEffect(() => {
    const pid = Number(displayProductId || 0);
    const name = String(displayProduct?.name ?? "").trim();
    if (!pid || !name) return;
    if (viewedProductIdRef.current === pid) return;
    viewedProductIdRef.current = pid;
    trackZbProductView(name, pid);
  }, [displayProductId, displayProduct?.name]);

  const configDirty = useMemo(
    () =>
      catalogService.CONFIG_KEYS.some(
        (k) => String(config[k] ?? "") !== String(initialConfigRef[k] ?? "")
      ),
    [config, initialConfigRef]
  );

  const variantDirty = selectedProductId !== initialProductId;
  const isDirty = variantDirty || configDirty;

  // Refetch with config only when user changed variant or config (isDirty). Avoids fetch on open and duplicate after merge.
  useEffect(() => {
    if (!displayProduct) {
      setIsVariantLoading(false);
      return;
    }
    if (!isDirty) {
      setIsVariantLoading(false);
      return;
    }

    let id = productId ?? displayProductId;
    if (selectedProductId) id = selectedProductId;
    if (!id) {
      setIsVariantLoading(false);
      return;
    }
    const timer = setTimeout(() => {
      variantRequestIdRef.current = id;
      const fetchWithConfig = getProduct
        ? Promise.resolve(getProduct(id, config, null)).then(
            (r) => r?.data ?? r
          )
        : catalogService.getCatalogProduct(id, config, null).then((r) => {
            const b = r?.data ?? r;
            return b?.data ?? b;
          });
      fetchWithConfig
        .then((data) => {
          if (!data || (!data.id && !data.product_id)) return;
          skipRefetchAfterMergeRef.current = true;
          setResolvedProduct((prev) => {
            if (!prev) return data;
            return { ...prev, ...data };
          });
        })
        .catch(() => {
          // No-op: variant loading will be cleared in .finally().
        })
        .finally(() => {
          if (variantRequestIdRef.current === id) {
            variantRequestIdRef.current = null;
            setIsVariantLoading(false);
            setFrozenDisplayProduct(null);
          }
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [
    config,
    productId,
    getProduct,
    selectedProductId,
    isDirty,
    displayProduct,
    displayProductId,
  ]);

  const handleVariantSelect = (productId) => {
    const nextId = String(productId);

    setFrozenDisplayProduct(displayProduct);
    setIsVariantLoading(true);
    setSelectedProductId(nextId);
  };

  const refetchProduct = useCallback(async () => {
    if (!displayProduct) return;
    const id = displayProduct.id ?? displayProduct.product_id;
    if (!id) return;
    try {
      const fetchProduct = getProduct
        ? getProduct(id)
        : catalogService.getCatalogProduct(id);
      const res = await Promise.resolve(fetchProduct);
      const body = res?.data ?? res;
      const data = body?.data ?? body;
      if (data && (data.id || data.product_id)) {
        setResolvedProduct((prev) => (prev ? { ...prev, ...data } : data));
      }
    } catch (err) {
      console.error(
        "Failed to reload product after adding to collection:",
        err
      );
    }
  }, [displayProduct, getProduct]);

  useEffect(() => {
    const projects = Array.isArray(displayProduct?.projects)
      ? displayProduct.projects
      : [];
    setProjectsList(
      sortStoreProjectsForBuyerAccount(
        projects,
        normalizeStoreUserId(currentUser)
      )
    );
  }, [displayProduct?.projects, currentUser?.id]);

  useEffect(() => {
    if (!isProjectPickerUser) return;

    let cancelled = false;
    void (async () => {
      try {
        const res = await getStoreProjects();
        const fetched = sortedStoreProjectsForBuyerAccount(
          res?.data ?? res,
          currentUser
        );
        if (cancelled) return;
        setProjectsList((prev) => {
          const merged = mergeBuyerAccountProjectLists(prev, fetched);
          return sortStoreProjectsForBuyerAccount(merged, currentUser);
        });
      } catch (_) {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isProjectPickerUser, currentUser?.id]);

  const pickerProduct = useMemo(() => {
    if (!displayProduct) return null;
    const rawId =
      selectedProductId || displayProduct.id || displayProduct.product_id;
    const id = Number(rawId);
    if (!Number.isFinite(id) || id <= 0) return displayProduct;
    return { ...displayProduct, id, product_id: id };
  }, [displayProduct, selectedProductId]);

  const openQuoteFolderPicker = useCallback(
    (projectId, projectName) => {
      if (productArchived) return;
      if (!displayProduct) return;
      const pid = toNullableNumber(projectId);
      if (pid == null) return;
      setAddError(null);
      setProjectDropdownOpen(false);
      setFolderPickerModal({
        isOpen: true,
        projectId: pid,
        projectName: String(projectName ?? "").trim() || `Project ${pid}`,
      });
    },
    [displayProduct, productArchived]
  );

  const handleFolderPickerAdded = useCallback(
    ({ projectId, productId, quantity }) => {
      const id = Number(productId);
      if (!Number.isFinite(id) || id <= 0) return;
      const qty = Number(quantity);
      const addCount = Number.isFinite(qty) && qty > 0 ? qty : 1;
      trackZbAddToCart(
        displayProduct?.name ?? "Product",
        id,
        displayProduct?.discount_price ?? displayProduct?.price ?? 0
      );
      setAddedProjectId(projectId);
      setAddedOnce(true);
      setNonCartAddedCount((n) => n + addCount);
      onAddToCart?.({ projectId, productId: id });
      setFolderPickerModal({
        isOpen: false,
        projectId: null,
        projectName: "",
      });
    },
    [
      displayProduct?.discount_price,
      displayProduct?.name,
      displayProduct?.price,
      onAddToCart,
    ]
  );

  const createProjectWithName = useCallback(
    async (rawProjectName) => {
      const projectName = String(rawProjectName ?? "").trim();
      if (!projectName) {
        throw new Error("Enter project name.");
      }
      setAddError(null);
      const res = await createStoreProject({ name: projectName });
      const body = res?.data ?? res;
      const project =
        body?.data?.project ?? body?.project ?? body?.data ?? body;
      const newId =
        project?.id ??
        project?.project_id ??
        body?.id ??
        body?.data?.id ??
        body?.data?.project?.id;

      if (!newId) {
        throw new Error("Project created but the response did not include an id.");
      }

      const listEntry =
        project && (project.id || project.project_id)
          ? project
          : {
              id: newId,
              name: projectName,
              can_manage_project_items: true,
            };

      setProjectsList((prev) =>
        sortStoreProjectsEarliestFirst([...prev, listEntry])
      );
      openQuoteFolderPicker(newId, projectName);
    },
    [openQuoteFolderPicker]
  );

  const handleAddProject = useCallback(() => {
    if (productArchived) return;
    setProjectDropdownOpen(false);
    setFolderPickerModal({
      isOpen: false,
      projectId: null,
      projectName: "",
    });
    const needsProjectName = shouldPromptStoreProjectName({
      projects: projectsList,
      hasProjects:
        displayProduct?.has_projects ??
        nonCartInitialHasProjects ??
        projectsList.length > 0,
    });
    if (!needsProjectName) {
      void createProjectWithName(MY_FIRST_QUOTE_PROJECT_NAME).catch((err) => {
        setAddError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to create project"
        );
      });
      return;
    }
    setCreateProjectNameModal({
      isOpen: true,
      draftName: "",
      busy: false,
      error: null,
    });
  }, [
    createProjectWithName,
    displayProduct?.has_projects,
    nonCartInitialHasProjects,
    productArchived,
    projectsList,
  ]);

  const submitCreateProjectNameModal = useCallback(async () => {
    const projectName = String(createProjectNameModal.draftName ?? "").trim();
    if (!projectName) {
      setCreateProjectNameModal((m) => ({
        ...m,
        error: "Enter project name.",
      }));
      return;
    }
    setCreateProjectNameModal((m) => ({ ...m, busy: true, error: null }));
    try {
      await createProjectWithName(projectName);
      setCreateProjectNameModal({
        isOpen: false,
        draftName: "",
        busy: false,
        error: null,
      });
    } catch (err) {
      setCreateProjectNameModal((m) => ({
        ...m,
        busy: false,
        error:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to create project",
      }));
    }
  }, [createProjectNameModal.draftName, createProjectWithName]);

  const ensureFirstProjectId = useCallback(async () => {
    let id = firstManageableProjectIdFromList(projectsList);
    if (id != null) return id;
    const res = await getStoreProjects();
    const fetched = sortedStoreProjectsForBuyerAccount(
      res?.data ?? res,
      currentUser
    );
    return firstManageableProjectIdFromList(fetched);
  }, [projectsList, currentUser]);

  const handleCartAdd = async () => {
    if (productArchived) return;
    const id =
      Number(selectedProductId) ||
      Number(displayProduct?.id ?? displayProduct?.product_id ?? 0);
    if (!id) return;
    setNonCartCartBusy(true);
    setAddError(null);
    try {
      let bannerProjectId = null;
      if (!hasProjectsFlag) {
        const cartResp = await addStoreProductToCartProject(id, {
          folderPath: productFolderPath,
          projectName: MY_FIRST_QUOTE_PROJECT_NAME,
        });
        const proj = cartResp?.data?.project ?? cartResp?.project ?? null;
        const newPid = Number(proj?.id ?? proj?.project_id ?? 0);
        if (proj != null && !Number.isNaN(newPid) && newPid > 0) {
          bannerProjectId = newPid;
          setProjectsList((prev) =>
            sortStoreProjectsEarliestFirst([
              proj,
              ...prev.filter(
                (p) => Number(p?.id ?? p?.project_id ?? 0) !== newPid
              ),
            ])
          );
        }
        trackZbAddToCart(
          displayProduct?.name ?? "Product",
          id,
          displayProduct?.discount_price ?? displayProduct?.price ?? 0
        );
      } else {
        const projectId = await ensureFirstProjectId();
        if (!projectId) throw new Error("No project found.");
        bannerProjectId = Number(projectId);
        const path = normalizeStoreProjectFolderPath(productFolderPath);
        await createStoreProjectItem(projectId, {
          item_type: "product",
          product_id: id,
          ...(path.length > 0 ? { folder_path: path } : {}),
        });
        trackZbAddToCart(
          displayProduct?.name ?? "Product",
          id,
          displayProduct?.discount_price ?? displayProduct?.price ?? 0
        );
      }
      setNonCartAddedCount((n) => n + 1);
      setAddedOnce(true);
      if (!isProjectPickerUser) {
        let pid = bannerProjectId;
        if (pid == null || Number.isNaN(Number(pid))) {
          pid = await ensureFirstProjectId();
        }
        if (pid != null && !Number.isNaN(Number(pid))) {
          setAddedProjectId(Number(pid));
        }
      }
    } catch (err) {
      setAddError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to add product to project"
      );
    } finally {
      setNonCartCartBusy(false);
    }
  };

  const handleAddToCollectionSubmit = async () => {
    const collectionId = selectedCollectionId
      ? String(selectedCollectionId).trim()
      : null;
    if (!collectionId) {
      setAddToCollectionError("Please select a collection.");
      return;
    }
    const productIdForApi =
      displayProduct?.id ?? displayProduct?.product_id ?? selectedProductId;
    if (!productIdForApi) return;
    setAddToCollectionSubmitting(true);
    setAddToCollectionError(null);
    try {
      await createCollectionProduct(collectionId, productIdForApi);
      setIsAddToCollectionModalOpen(false);
      setSelectedCollectionId("");
      await refetchProduct();
    } catch (err) {
      setAddToCollectionError(
        err?.error ?? err?.message ?? "Failed to add product to collection."
      );
    } finally {
      setAddToCollectionSubmitting(false);
    }
  };

  // While variant is loading, show frozen product/selection so nothing in the modal changes until load completes.
  const productToShow =
    isVariantLoading && frozenDisplayProduct
      ? frozenDisplayProduct
      : displayProduct;

  const productToShowId = productToShow?.id ?? productToShow?.product_id;

  const modalVariantAxes = useStableNewVariantAxes(productToShow);
  const modalSizeAxes = resolveVariantSizeAxes(modalVariantAxes);
  const modalSizePids = pickVariantSizeProductIds(
    modalVariantAxes,
    productToShowId,
  );
  const deliveryText = getDeliveryTextForSelectedVariant({
    currentProduct: productToShow,
    heightOpts: modalSizeAxes.heightOpts,
    widthOpts: modalSizeAxes.widthOpts,
    depthOpts: modalSizeAxes.depthOpts,
    heightPid: modalSizePids.heightPid,
    widthPid: modalSizePids.widthPid,
    depthPid: modalSizePids.depthPid,
  });

  const showAxisVariantUi =
    Boolean(productToShow) &&
    ((modalVariantAxes?.range?.length ?? 0) > 0 ||
      (modalVariantAxes?.size?.height?.length ?? 0) > 0 ||
      (modalVariantAxes?.size?.width?.length ?? 0) > 0 ||
      (modalVariantAxes?.size?.depth?.length ?? 0) > 0);

  const productNameToShow = productToShow?.name ?? "Product";

  const packingRows = useMemo(
    () =>
      normalizePackingRows(
        productToShow?.contents ?? displayProduct?.contents,
        productToShow?.packing_list ?? displayProduct?.packing_list
      ),
    [productToShow?.contents, displayProduct?.contents]
  );

  const packingListRows = useMemo(
    () =>
      normalizePackingListRows(
        productToShow?.packing_list ?? displayProduct?.packing_list
      ),
    [productToShow?.packing_list, displayProduct?.packing_list]
  );

  const shutterPanels = useMemo(
    () =>
      normalizeShutterPanels(
        productToShow?.shutter_panels ?? displayProduct?.shutter_panels
      ),
    [productToShow?.shutter_panels, displayProduct?.shutter_panels]
  );

  const cutlistRows = useMemo(
    () =>
      normalizeCutlistRows(
        productToShow?.cutlist_rows ?? displayProduct?.cutlist_rows
      ),
    [productToShow?.cutlist_rows, displayProduct?.cutlist_rows]
  );

  const shutterCutlistRows = useMemo(
    () =>
      normalizeShutterCutlistRows(
        productToShow?.shutter_cutlist_rows ??
          displayProduct?.shutter_cutlist_rows
      ),
    [
      productToShow?.shutter_cutlist_rows,
      displayProduct?.shutter_cutlist_rows,
    ]
  );

  const showAdminCutlistLink =
    isAdminUser && (cutlistRows.length > 0 || shutterCutlistRows.length > 0);

  const productImageUrls = useMemo(
    () => getProductImageUrls(productToShow),
    [productToShow]
  );

  const priceSource = isVariantLoading ? productToShow : displayProduct;
  const productListPrice = priceSource?.price ?? null;
  const productDiscountPrice = priceSource?.discount_price ?? null;
  const productSqftPrice = priceSource?.sqft_price ?? null;
  const productPrice = productDiscountPrice ?? productListPrice;
  const productStrikethroughPrice =
    productDiscountPrice != null &&
    productListPrice != null &&
    Number(productDiscountPrice) !== Number(productListPrice)
      ? productListPrice
      : null;

  const productFolderPath = Array.isArray(productToShow?.folder_path)
    ? productToShow.folder_path
    : Array.isArray(nonCartInitialFolderPath)
    ? nonCartInitialFolderPath
    : [];
  const highlightFolderLinks = useMemo(
    () => normalizeHighlightFolderLinks(productToShow?.highlight_folders ?? []),
    [productToShow?.highlight_folders]
  );

  const featuredHighlightFolderLinks = useMemo(
    () => highlightFolderLinks.slice(0, 6),
    [highlightFolderLinks]
  );
  const hasProjectsFlag =
    productToShow?.has_projects ??
    displayProduct?.has_projects ??
    nonCartInitialHasProjects ??
    projectsList.length > 0;
  const addToQuoteDraftQuotes = useMemo(
    () => filterOwnStoreDraftQuotesForAdd(projectsList, currentUser),
    [projectsList, currentUser]
  );
  const oneDraftQuoteId =
    addToQuoteDraftQuotes.length === 1
      ? toNullableNumber(
          addToQuoteDraftQuotes[0]?.id ?? addToQuoteDraftQuotes[0]?.project_id
        )
      : null;

  const oneDraftQuoteName = useMemo(() => {
    if (addToQuoteDraftQuotes.length !== 1) return "";
    const row = addToQuoteDraftQuotes[0];
    return String(row?.name ?? row?.project_name ?? "").trim();
  }, [addToQuoteDraftQuotes]);

  useEffect(() => {
    const n = Number(
      productToShow?.added ?? displayProduct?.added ?? nonCartInitialAdded ?? 0
    );
    setNonCartAddedCount(Number.isNaN(n) ? 0 : n);
  }, [
    productToShow?.added,
    displayProduct?.added,
    nonCartInitialAdded,
    productToShowId,
  ]);

  useEffect(() => {
    setCarouselIndex(0);
  }, [productToShowId]);

  // Notify parent when user selects a different variant so URL can update.
  // In code routes, `productId` prop is a product code string, so compare against the
  // currently displayed numeric id first to avoid re-trigger loops.
  useEffect(() => {
    const compareAgainstId = displayProductId;
    if (
      isOpen &&
      selectedProductId &&
      compareAgainstId != null &&
      String(selectedProductId) !== String(compareAgainstId)
    ) {
      onProductIdChange?.(selectedProductId);
    }
  }, [
    isOpen,
    selectedProductId,
    displayProductId,
    productId,
    onProductIdChange,
  ]);

  if (!isOpen) return null;

  const carouselNext = () => {
    if (carouselAnimating || productImageUrls.length <= 1) return;
    setCarouselIndex((i) => (i === productImageUrls.length - 1 ? 0 : i + 1));
  };

  const carouselPrev = () => {
    if (carouselAnimating || productImageUrls.length <= 1) return;
    setCarouselIndex((i) => (i === 0 ? productImageUrls.length - 1 : i - 1));
  };

  const carouselGoTo = (idx) => {
    if (!carouselAnimating) setCarouselIndex(idx);
  };

  const handleCarouselTouchStart = (e) => {
    if (!isMobile) return;
    touchStartXRef.current = e.touches?.[0]?.clientX ?? null;
  };

  const handleCarouselTouchEnd = (e) => {
    if (!isMobile || productImageUrls.length <= 1) return;
    const startX = touchStartXRef.current;
    const endX = e.changedTouches?.[0]?.clientX ?? null;
    touchStartXRef.current = null;
    if (startX == null || endX == null) return;
    const deltaX = endX - startX;
    if (Math.abs(deltaX) < 30) return;
    // Requested behavior: swipe left -> previous, swipe right -> next.
    if (deltaX < 0) {
      carouselPrev();
    } else {
      carouselNext();
    }
  };

  const handleModalTouchStartCapture = (e) => {
    if (!isMobile) return;
    const touch = e.touches?.[0];
    if (!touch) return;
    edgeSwipeStartXRef.current = touch.clientX;
    edgeSwipeStartYRef.current = touch.clientY;
  };

  const handleModalTouchEndCapture = (e) => {
    if (!isMobile) return;
    const touch = e.changedTouches?.[0];
    const startX = edgeSwipeStartXRef.current;
    const startY = edgeSwipeStartYRef.current;
    edgeSwipeStartXRef.current = null;
    edgeSwipeStartYRef.current = null;
    if (touch == null || startX == null || startY == null) return;

    const deltaX = touch.clientX - startX;
    const deltaY = Math.abs(touch.clientY - startY);
    const startedFromLeftEdge = startX <= 24;
    if (startedFromLeftEdge && deltaX > 72 && deltaY < 60) {
      onClose?.();
    }
  };

  const handleMobileSearchFocus = () => {
    navigate("/products", { replace: true, state: { focusSearch: true } });
  };

  const getProductDetailImageOverlayText = (imageIndex) => {
    if (imageIndex === 0) {
      return "Height excludes plinth/legs";
    }
    if (imageIndex === 1) {
      return "Shutters shown for representation only. Not included";
    }
    return "Reference photos of a Prime-Minifix product";
  };

  const imageBlock = productToShow && (
    <div
      className={`flex-shrink-0 text-center rounded p-2 d-flex flex-column align-items-center position-relative${
        isMobile ? " w-100 overflow-hidden" : " align-self-start"
      }`}
      style={{
        minHeight: isMobile ? 220 : 160,
        ...(!isMobile ? { width: "40%" } : {}),
        background: "linear-gradient(180deg, #FAF8F3 0%, #E6E0D5 100%)",
        border: "1px solid #e3dccf",
      }}
    >
      {productImageUrls.length === 0 ? (
        <div
          className="d-flex align-items-center justify-content-center text-muted bg-white rounded w-100"
          style={{ minHeight: isMobile ? 180 : 140, maxWidth: 280 }}
        >
          No image
        </div>
      ) : (
        <>
          <Carousel
            activeIndex={carouselIndex}
            next={carouselNext}
            previous={carouselPrev}
            interval={false}
            slide={false}
            className={`w-100 flex-grow-1 d-flex align-items-start${
              isMobile ? " overflow-hidden" : ""
            }`}
            onTouchStart={handleCarouselTouchStart}
            onTouchEnd={handleCarouselTouchEnd}
          >
            {productImageUrls.map((url, idx) => (
              <CarouselItem
                key={idx}
                onExiting={() => setCarouselAnimating(true)}
                onExited={() => setCarouselAnimating(false)}
              >
                <div
                  className={`d-flex flex-column align-items-center p-1${
                    isMobile ? " overflow-hidden" : ""
                  }`}
                >
                  <img
                    src={url}
                    alt={`${productNameToShow} ${idx + 1}`}
                    className="mw-100 object-fit-contain"
                    style={{ width: "auto", maxHeight: "45vh" }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = STORE_MISSING_IMAGE_URL;
                    }}
                  />
                  <div
                    style={{
                      marginTop: "10px",
                      border: "1px solid #DDD",
                      background: "rgba(0,0,0,0.4)",
                      padding: "1px 5px",
                      borderRadius: "7px",
                      color: "#FFF",
                    }}
                  >
                    {getProductDetailImageOverlayText(idx)}
                  </div>
                </div>
              </CarouselItem>
            ))}
            <CarouselControl
              direction="prev"
              directionText="Previous"
              onClickHandler={carouselPrev}
            />
            <CarouselControl
              direction="next"
              directionText="Next"
              onClickHandler={carouselNext}
            />
          </Carousel>
          <div
            className="product-detail-carousel-dots mt-2"
            role="tablist"
            aria-label="Product images"
          >
            {productImageUrls.map((_, idx) => {
              const active = idx === carouselIndex;
              return (
                <button
                  key={`dot-${idx}`}
                  type="button"
                  className={
                    active
                      ? "product-detail-carousel-dot is-active"
                      : "product-detail-carousel-dot"
                  }
                  onClick={() => carouselGoTo(idx)}
                  aria-label={`Show image ${idx + 1}`}
                  aria-selected={active}
                  role="tab"
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      <Modal
        isOpen
        toggle={onClose}
        size="lg"
        className={`product-detail-modal${
          useZbColumnModal ? " product-detail-modal--zb-column" : ""
        }`}
        fullscreen={!useZbColumnModal}
      >
        {!isMobile ? (
          <ModalHeader
            toggle={onClose}
            className="product-detail-modal__close-header border-0 flex-shrink-0 py-2"
          >
            <span className="visually-hidden">Product details</span>
          </ModalHeader>
        ) : null}
        <ModalBody
          className={
            isMobile
              ? "position-static overflow-auto flex-grow-1"
              : "position-relative"
          }
          onTouchStartCapture={handleModalTouchStartCapture}
          onTouchEndCapture={handleModalTouchEndCapture}
        >
          {isMobile ? (
            <div
              className="position-sticky bg-white d-flex align-items-center gap-2 px-1 py-2 shadow-sm"
              style={{
                zIndex: 1060,
                top: "-1rem",
                margin: "-1rem -1rem 0 -1rem",
              }}
            >
              <button
                type="button"
                className="btn btn-link text-dark p-2 text-decoration-none"
                onClick={onClose}
                aria-label="Back"
              >
                <i className="fa-solid fa-arrow-left fs-5" />
              </button>
              <div
                className="d-flex align-items-center flex-grow-1 border rounded-pill px-2 py-1 bg-white"
                style={{ minHeight: 36 }}
              >
                <img
                  src={searchLogoPath}
                  alt="ZeroBuild"
                  height="18"
                  className="me-2 flex-shrink-0"
                />
                <input
                  type="text"
                  value=""
                  readOnly
                  aria-label="Search kitchens, wardrobes, etc."
                  placeholder="Search kitchens, wardrobes, etc."
                  className="border-0 flex-grow-1 bg-transparent"
                  style={{ outline: "none", fontSize: "0.9rem" }}
                  onFocus={handleMobileSearchFocus}
                />
                <i className="fa-solid fa-magnifying-glass text-secondary ms-2" />
              </div>
            </div>
          ) : null}
          {displayProduct && isVariantLoading ? (
            <div
              className="position-absolute d-flex flex-column align-items-center justify-content-center gap-2"
              style={{
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1055,
                backgroundColor: "rgba(255,255,255,0.65)",
                pointerEvents: "auto",
              }}
              aria-busy="true"
              aria-live="polite"
            >
              <Spinner color="primary" />
              <span className="text-muted small fw-semibold">Updating…</span>
            </div>
          ) : null}
          {productId && !displayProduct && loadingProduct && (
            <div className="text-center py-5">
              <Spinner />
              <p className="mt-2 text-muted">Loading product...</p>
            </div>
          )}
          {productId && !displayProduct && loadError && !loadingProduct && (
            <Alert color="danger">
              {loadError}
              <Button color="link" className="p-0 ms-2" onClick={onClose}>
                Close
              </Button>
            </Alert>
          )}
          {displayProduct && (
            <div
              className={`d-flex flex-column flex-md-row gap-4 position-relative pt-3 ${
                isMobile ? " overflow-visible" : ""
              }`}
            >
              {imageBlock}
              <div className="flex-grow-1 min-w-0 overflow-visible">
                <h2 className="mb-2 fw-bold fs-6">{productNameToShow}</h2>
                {(!isLoggedIn ||
                  productPrice != null ||
                  productListPrice != null) && (
                  <p className="text-primary mb-2 fs-6">
                    {isInternalAccess() && productToShow?.price_meta ? (
                      <>
                        <span
                          id="product-detail-price-popover"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPricePopoverOpen((prev) => !prev);
                          }}
                        >
                          <PriceOrLogin
                            className="fs-4"
                            price={productPrice ?? productListPrice}
                            strikethroughPrice={productStrikethroughPrice}
                            sqftPrice={productSqftPrice}
                          />
                        </span>
                        <Popover
                          placement="auto"
                          isOpen={pricePopoverOpen}
                          target="product-detail-price-popover"
                          toggle={() => setPricePopoverOpen(false)}
                        >
                          <PopoverBody
                            className="mw-100"
                            style={{ minWidth: 480 }}
                          >
                            <ProductPriceBreakdown
                              price_meta={productToShow.price_meta}
                              totalPrice={productPrice ?? productListPrice}
                              is_discounted={productToShow?.is_discounted}
                              original_price={productToShow?.original_price}
                            />
                          </PopoverBody>
                        </Popover>
                      </>
                    ) : (
                      isLoggedIn && (
                        <PriceOrLogin
                          price={productPrice ?? productListPrice}
                          strikethroughPrice={productStrikethroughPrice}
                          sqftPrice={productSqftPrice}
                        />
                      )
                    )}
                  </p>
                )}
                <div
                  className="text-success mt-1 d-flex align-items-center gap-1 fw-bold mb-2"
                  style={{ fontSize: "0.85rem" }}
                >
                  <i className="fa-solid fa-truck" aria-hidden />
                  Shipping <span>{deliveryText}</span>
                  <small className="text-muted fw-normal">(Chennai)</small>
                </div>
                <div className={isMobile ? "mb-3" : "mb-4"}>
                  {showAxisVariantUi && (
                    <div className="mb-3 border bg-light rounded">
                      <ProductDetailModalNewShapeVariants
                        product={productToShow}
                        disabled={loadingProduct || isVariantLoading}
                        onPickProductId={handleVariantSelect}
                      />
                      {productToShow?.warranty?.title && (
                        <div
                          className="px-3 py-2 d-flex justify-content-between align-items-center border-top mt-2"
                          style={{ background: "#EEE" }}
                        >
                          <div className="d-flex align-items-center gap-1">
                            <span className="product-detail-warranty-row__title">
                              {productToShow.warranty.title}
                            </span>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 product-detail-warranty-row__link"
                              onClick={() => setIsWarrantyTermsOpen(true)}
                            >
                              (Terms)
                            </button>
                          </div>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 product-detail-warranty-row__link"
                            onClick={() => setIsSizingGridOpen(true)}
                          >
                            Size Chart
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <StoreDeliveryChargesInfo className="mb-3" />

                  {shutterPanels.length > 0 && (
                    <div className="card p-2 mb-2 d-block rounded-0">
                      <span className="fw-bold">Shutters not included.</span>{" "}
                      You can download shutter cutlist when placing the order -
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 align-baseline text-decoration-none"
                        onClick={() => setIsShuttersModalOpen(true)}
                      >
                        View Shutter Cutlist
                      </button>
                    </div>
                  )}
                  {Array.isArray(productToShow?.spec) &&
                    productToShow.spec.length > 0 && (
                      <div className="mb-3">
                        <div className="fw-bold mb-2 fs-6">Specifications</div>
                        <table className="table table-bordered mb-0">
                          <tbody>
                            {productToShow.spec.map((row, idx) => {
                              const key = row?.key;
                              const value = row?.value;
                              const keyNorm = String(key ?? "")
                                .trim()
                                .toLowerCase()
                                .replace(/\s+/g, "_");
                              if (key == null || key === "") return null;
                              if (value == null || value === "") return null;
                              if (keyNorm === "range") return null;

                              if (keyNorm === "core_material") {
                                const coreMaterialLabel =
                                  CORE_MATERIAL_DISPLAY[value] ?? String(value);
                                const sampleName = String(
                                  row?.sample_name ?? ""
                                ).trim();
                                const sampleUrlRaw = String(
                                  row?.sample_url ?? ""
                                ).trim();
                                return (
                                  <tr
                                    key={`${key}-${idx}`}
                                    className={
                                      idx % 2 === 0 ? "table-light" : ""
                                    }
                                  >
                                    <td
                                      className="fw-semibold"
                                      style={{ width: "40%" }}
                                    >
                                      Core Material
                                    </td>
                                    <td>
                                      {coreMaterialLabel}
                                      {sampleUrlRaw ? (
                                        <div className="small text-muted mt-1">
                                          <button
                                            type="button"
                                            className="btn btn-link btn-sm p-0 align-baseline"
                                            onClick={() =>
                                              setSamplePreview({
                                                isOpen: true,
                                                name: coreMaterialLabel,
                                                url: resolvePublicRelativeUrl(
                                                  sampleUrlRaw
                                                ),
                                              })
                                            }
                                          >
                                            view sample
                                          </button>
                                        </div>
                                      ) : null}
                                    </td>
                                  </tr>
                                );
                              }

                              if (keyNorm === "color") {
                                const color = COLOR_DISPLAY[value];
                                const sampleName = String(
                                  row?.sample_name ?? ""
                                ).trim();
                                const sampleUrlRaw = String(
                                  row?.sample_url ?? ""
                                ).trim();
                                return (
                                  <tr
                                    key={`${key}-${idx}`}
                                    className={
                                      idx % 2 === 0 ? "table-light" : ""
                                    }
                                  >
                                    <td className="fw-semibold align-middle">
                                      Color
                                    </td>
                                    <td>
                                      {color ? (
                                        <div className="d-flex align-items-center gap-2">
                                          <img
                                            src={`/store/finish/${color.finish}.webp`}
                                            alt={color.name}
                                            className="rounded-circle border object-fit-cover"
                                            style={{ width: 24, height: 24 }}
                                          />
                                          <span className="small">
                                            {color.name}
                                          </span>
                                        </div>
                                      ) : (
                                        String(value)
                                      )}
                                      {sampleName && sampleUrlRaw ? (
                                        <div className="small text-muted mt-1">
                                          {"Matches "}
                                          <strong>{sampleName}</strong>
                                          {" - "}
                                          <button
                                            type="button"
                                            className="btn btn-link btn-sm p-0 align-baseline"
                                            onClick={() =>
                                              setSamplePreview({
                                                isOpen: true,
                                                name: String(value),
                                                url: resolvePublicRelativeUrl(
                                                  sampleUrlRaw
                                                ),
                                              })
                                            }
                                          >
                                            view sample
                                          </button>
                                        </div>
                                      ) : null}
                                    </td>
                                  </tr>
                                );
                              }

                              return (
                                <tr
                                  key={`${String(key)}-${idx}`}
                                  className={idx % 2 === 0 ? "table-light" : ""}
                                >
                                  <td className="fw-semibold align-middle">
                                    {String(key)}
                                  </td>
                                  <td>{String(value)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  {packingRows.length > 0 && (
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <div className="fw-bold fs-6">What's included?</div>
                        {showAdminCutlistLink && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 align-baseline"
                            onClick={() => setIsCutlistModalOpen(true)}
                          >
                            View Cutlist
                          </button>
                        )}
                      </div>
                      <table className="table table-bordered table-sm product-detail-packing-table">
                        <thead className="table-secondary">
                          <tr>
                            <th scope="col">Item</th>
                            <th scope="col" className="text-end">
                              Qty
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            return packingRows.map((row, index) => {
                              const itemLabel = String(row.item ?? "").trim();
                              const isPanelRow =
                                itemLabel.toLowerCase() === "panels";

                              return (
                                <tr
                                  key={index}
                                  className={
                                    index % 2 === 0 ? "table-light" : ""
                                  }
                                >
                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      <span>
                                        {row.item}
                                        {row.sku ? (
                                          <span className="text-muted">
                                            {" "}
                                            ({row.sku})
                                          </span>
                                        ) : null}
                                      </span>
                                      {isPanelRow && (
                                        <button
                                          type="button"
                                          className="btn btn-link btn-sm p-0 align-baseline"
                                          onClick={() =>
                                            setIsPackingListModalOpen(true)
                                          }
                                        >
                                          View packing list
                                        </button>
                                      )}
                                    </div>
                                    {row.itemDetail ? (
                                      <div className="small text-muted">
                                        {row.itemDetail}
                                      </div>
                                    ) : null}
                                  </td>
                                  <td className="text-end">{row.count}</td>
                                </tr>
                              );
                            });
                          })()}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                {addError && (
                  <Alert color="danger" className="small">
                    {addError}
                  </Alert>
                )}
                {addedOnce && (
                  <div className="mb-2 p-2 rounded bg-success bg-opacity-10 border border-success small">
                    <span className="text-success fw-semibold">
                      {isProjectPickerUser
                        ? "Added to your project."
                        : "Added to your quote."}
                    </span>
                    {addedProjectId != null && (
                      <Link
                        to={`/projects/${addedProjectId}`}
                        className="ms-2 small"
                        onClick={() => onClose?.()}
                      >
                        {isProjectPickerUser ? "View project" : "View quote"}
                      </Link>
                    )}
                  </div>
                )}
                <div className="mt-3">
                  {productArchived ? (
                    <Alert
                      color="warning"
                      className="small py-2 mb-2"
                      fade={false}
                    >
                      This product is archived and cannot be added to a quote.
                    </Alert>
                  ) : null}
                  {isDealerUser || isAdminUser ? (
                    <p className="text-muted small mb-0">
                      Catalog is view-only for{" "}
                      {isAdminUser ? "admin" : "dealer"} accounts.
                    </p>
                  ) : isCustomerUser ? (
                    <span id={addToQuoteTooltipId} className="d-inline-block">
                      {addToQuoteDraftQuotes.length > 1 ? (
                        <div
                          className="btn-group product-detail-split-group"
                          role="group"
                        >
                          <Dropdown
                            isOpen={projectDropdownOpen}
                            toggle={() =>
                              setProjectDropdownOpen((prev) => !prev)
                            }
                            className="product-detail-project-dropdown"
                            direction="up"
                          >
                            <DropdownToggle
                              color="primary"
                              className="product-detail-add-to-cart-btn product-detail-split-main"
                              disabled={addToQuoteDisabled}
                            >
                              Add to Quote
                            </DropdownToggle>
                            <DropdownMenu
                              flip={false}
                              className="product-detail-project-dropdown-menu"
                            >
                              <StoreBuyerQuotePickerItems
                                quotes={addToQuoteDraftQuotes}
                                currentUser={currentUser}
                                placement="menu"
                                renderItem={(p, displayName) => {
                                  const pid = p?.id ?? p?.project_id;
                                  return (
                                    <DropdownItem
                                      key={String(pid)}
                                      toggle={false}
                                      onClick={() =>
                                        openQuoteFolderPicker(pid, displayName)
                                      }
                                    >
                                      {displayName}
                                    </DropdownItem>
                                  );
                                }}
                              />
                              <DropdownItem divider toggle={false} />
                              <DropdownItem
                                toggle={false}
                                onClick={() => {
                                  setProjectDropdownOpen(false);
                                  window.setTimeout(() => handleAddProject(), 0);
                                }}
                              >
                                Add new quote
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                          <Button
                            color="primary"
                            className="product-detail-add-to-cart-btn product-detail-split-arrow"
                            aria-label="Create New Quote"
                            disabled={addToQuoteDisabled}
                            onClick={handleAddProject}
                          >
                            <i
                              className="fa-solid fa-chevron-down"
                              aria-hidden
                            />
                          </Button>
                        </div>
                      ) : addToQuoteDraftQuotes.length === 1 ? (
                        <div
                          className="btn-group product-detail-split-group"
                          role="group"
                        >
                          <Button
                            color="primary"
                            className="product-detail-add-to-cart-btn product-detail-split-main"
                            disabled={addToQuoteDisabled}
                            onClick={() => {
                              if (oneDraftQuoteId != null) {
                                openQuoteFolderPicker(
                                  oneDraftQuoteId,
                                  oneDraftQuoteName
                                );
                                return;
                              }
                              handleAddProject();
                            }}
                          >
                            Add to Quote
                          </Button>
                          <Button
                            color="primary"
                            className="product-detail-add-to-cart-btn product-detail-split-arrow"
                            aria-label="Create New Quote"
                            disabled={addToQuoteDisabled}
                            onClick={handleAddProject}
                          >
                            <i
                              className="fa-solid fa-chevron-down"
                              aria-hidden
                            />
                          </Button>
                        </div>
                      ) : (
                        <div
                          className="btn-group product-detail-split-group"
                          role="group"
                        >
                          <Button
                            color="primary"
                            className="product-detail-add-to-cart-btn product-detail-split-main"
                            disabled={addToQuoteDisabled}
                            onClick={handleAddProject}
                          >
                            Add to Quote
                          </Button>
                          <Button
                            color="primary"
                            className="product-detail-add-to-cart-btn product-detail-split-arrow"
                            aria-label="Create New Quote"
                            disabled={addToQuoteDisabled}
                            onClick={handleAddProject}
                          >
                            <i
                              className="fa-solid fa-chevron-down"
                              aria-hidden
                            />
                          </Button>
                        </div>
                      )}
                      {productArchived ? (
                        <UncontrolledTooltip target={addToQuoteTooltipId}>
                          This product is archived and cannot be added to a
                          quote.
                        </UncontrolledTooltip>
                      ) : null}
                    </span>
                  ) : isProjectPickerUser ? (
                    <span id={addToQuoteTooltipId} className="d-inline-block">
                      <Button
                        color="primary"
                        className="product-detail-add-to-cart-btn"
                        disabled={addToQuoteDisabled}
                        onClick={() => {
                          if (oneDraftQuoteId != null) {
                            openQuoteFolderPicker(
                              oneDraftQuoteId,
                              oneDraftQuoteName
                            );
                            return;
                          }
                          handleAddProject();
                        }}
                      >
                        Add to Quote
                      </Button>
                      {productArchived ? (
                        <UncontrolledTooltip target={addToQuoteTooltipId}>
                          This product is archived and cannot be added to a
                          quote.
                        </UncontrolledTooltip>
                      ) : null}
                    </span>
                  ) : (
                    <span id={addToQuoteTooltipId} className="d-inline-block">
                      <Button
                        color="primary"
                        className="product-detail-add-to-cart-btn"
                        disabled={
                          nonCartCartBusy ||
                          loadingProduct ||
                          isVariantLoading ||
                          productArchived
                        }
                        onClick={() => {
                          if (!isLoggedIn) {
                            openAuthModal();
                            return;
                          }
                          handleCartAdd();
                        }}
                      >
                        {nonCartCartBusy ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Adding...
                          </>
                        ) : isLoggedIn ? (
                          "Add to Quote"
                        ) : (
                          "View price"
                        )}
                      </Button>
                      {productArchived ? (
                        <UncontrolledTooltip target={addToQuoteTooltipId}>
                          This product is archived and cannot be added to a
                          quote.
                        </UncontrolledTooltip>
                      ) : null}
                    </span>
                  )}
                </div>
                {highlightFolderLinks.length > 0 ? (
                  <div className="mt-4 border-top pt-3">
                    <div className="fw-semibold mb-1">
                      Explore more categories
                    </div>
                    <div className="small text-muted mb-2">
                      Popular in 1-day delivery
                    </div>
                    <div
                      className="d-flex flex-wrap gap-2"
                      aria-label="Highlight folders"
                    >
                      {featuredHighlightFolderLinks.map((folder, idx) => {
                        const key = `${folder.label}-${idx}`;
                        const content = (
                          <>
                            <div
                              className="rounded-2 p-1 overflow-hidden border"
                              style={{
                                background:
                                  "linear-gradient(180deg, #FAF8F3 0%, #E6E0D5 100%)",
                                border: "1px solid #e3dccf",
                              }}
                            >
                              {folder.imageUrl ? (
                                <img
                                  src={folder.imageUrl}
                                  alt={folder.label}
                                  className="w-100 object-fit-contain d-block"
                                  style={{ height: "60px" }}
                                  loading="lazy"
                                />
                              ) : (
                                <div
                                  className="d-flex align-items-center justify-content-center text-muted small"
                                  style={{ height: "60px" }}
                                >
                                  No image
                                </div>
                              )}
                            </div>
                            <div className="small text-secondary mt-1 text-truncate">
                              {folder.label}
                            </div>
                          </>
                        );
                        if (folder.href.startsWith("/")) {
                          return (
                            <Link
                              key={key}
                              to={folder.href}
                              onClick={() => onClose?.()}
                              className="text-decoration-none text-center"
                              style={{ width: "60px" }}
                            >
                              {content}
                            </Link>
                          );
                        }
                        return (
                          <a
                            key={key}
                            href={folder.href || "/products"}
                            className="text-decoration-none text-center"
                            style={{ width: "60px" }}
                          >
                            {content}
                          </a>
                        );
                      })}
                    </div>
                    <div className="mt-2">
                      <Link
                        to="/products"
                        onClick={() => onClose?.()}
                        className="btn btn-sm btn-outline-primary"
                      >
                        View all categories
                      </Link>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>

      {displayProduct && (
        <Modal
          isOpen={isAddToCollectionModalOpen}
          toggle={() => {
            setIsAddToCollectionModalOpen(false);
            setAddToCollectionError(null);
            setSelectedCollectionId("");
          }}
          size="sm"
        >
          <ModalHeader
            toggle={() => {
              setIsAddToCollectionModalOpen(false);
              setAddToCollectionError(null);
              setSelectedCollectionId("");
            }}
          >
            Add to Collection
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="add-to-collection-select">Collection</Label>
              <Input
                id="add-to-collection-select"
                type="select"
                value={selectedCollectionId}
                onChange={(e) => setSelectedCollectionId(e.target.value)}
              >
                <option value="">Select a collection…</option>
                {(displayProduct.collections || []).map((c) => {
                  const id = c?.id ?? c;
                  const name = c?.name ?? String(id);
                  const mappedIds = displayProduct.mapped_collection_ids || [];
                  const isMapped = mappedIds.some(
                    (mid) => String(mid) === String(id)
                  );
                  return (
                    <option key={id} value={id} disabled={isMapped}>
                      {name}
                      {isMapped ? " (already added)" : ""}
                    </option>
                  );
                })}
              </Input>
            </FormGroup>
            {addToCollectionError && (
              <Alert color="danger" className="small mt-2">
                {addToCollectionError}
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              outline
              onClick={() => {
                setIsAddToCollectionModalOpen(false);
                setAddToCollectionError(null);
                setSelectedCollectionId("");
              }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleAddToCollectionSubmit}
              disabled={!selectedCollectionId || addToCollectionSubmitting}
            >
              {addToCollectionSubmitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving…
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      <Modal
        isOpen={isPackingListModalOpen}
        toggle={() => setIsPackingListModalOpen(false)}
        centered
        className={useZbColumnModal ? "store-zb-column-modal" : ""}
      >
        <ModalHeader toggle={() => setIsPackingListModalOpen(false)}>
          Packing List
        </ModalHeader>
        <ModalBody>
          {packingListRows.length > 0 ? (
            <table className="table table-bordered table-sm mb-0">
              <thead className="table-secondary">
                <tr>
                  <th scope="col">Label</th>
                  <th scope="col">Panels</th>
                </tr>
              </thead>
              <tbody>
                {packingListRows.map((row, idx) => (
                  <tr
                    key={`${row.label}-${idx}`}
                    className={idx % 2 === 0 ? "table-light" : ""}
                  >
                    <td>{row.label}</td>
                    <td>{row.panels}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-muted">No packing list available.</div>
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={isCutlistModalOpen}
        toggle={() => setIsCutlistModalOpen(false)}
        size="xl"
        className={useZbColumnModal ? "store-zb-column-modal" : ""}
      >
        <ModalHeader toggle={() => setIsCutlistModalOpen(false)}>
          Cutlist
        </ModalHeader>
        <ModalBody className="overflow-auto">
          {cutlistRows.length > 0 && (
            <div className="mb-4">
              <div className="fw-semibold mb-2">Panels</div>
              <table className="table table-bordered table-sm mb-0">
                <thead className="table-secondary">
                  <tr>
                    {PRODUCT_CUTLIST_COLUMNS.map((column) => (
                      <th key={column.key} scope="col" className="text-nowrap">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cutlistRows.map((row, idx) => (
                    <tr
                      key={`${row.fb_name}-${idx}`}
                      className={idx % 2 === 0 ? "table-light" : ""}
                    >
                      {PRODUCT_CUTLIST_COLUMNS.map((column) => (
                        <td key={column.key} className="text-nowrap">
                          {row[column.key] == null || row[column.key] === ""
                            ? "-"
                            : row[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {shutterCutlistRows.length > 0 && (
            <div>
              <div className="fw-semibold mb-2">Shutters</div>
              <table className="table table-bordered table-sm mb-0">
                <thead className="table-secondary">
                  <tr>
                    {PRODUCT_SHUTTER_CUTLIST_COLUMNS.map((column) => (
                      <th key={column.key} scope="col" className="text-nowrap">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shutterCutlistRows.map((row, idx) => (
                    <tr
                      key={`${row.label}-${idx}`}
                      className={idx % 2 === 0 ? "table-light" : ""}
                    >
                      {PRODUCT_SHUTTER_CUTLIST_COLUMNS.map((column) => (
                        <td key={column.key} className="text-nowrap">
                          {row[column.key] == null || row[column.key] === ""
                            ? "-"
                            : row[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {cutlistRows.length === 0 && shutterCutlistRows.length === 0 && (
            <div className="text-muted">No cutlist available.</div>
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={isShuttersModalOpen}
        toggle={() => setIsShuttersModalOpen(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setIsShuttersModalOpen(false)}>
          Shutter Cutting List
        </ModalHeader>
        <ModalBody>
          {shutterPanels.length > 0 ? (
            <>
              <table className="table table-bordered table-sm mb-0">
                <thead className="table-secondary">
                  <tr>
                    <th scope="col">Label</th>
                    <th scope="col">
                      Length{" "}
                      <div className="small text-muted fw-normal">
                        in mm (incl. edges)
                      </div>
                    </th>
                    <th scope="col">
                      Width{" "}
                      <div className="small text-muted fw-normal">
                        in mm (incl. edges)
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shutterPanels.map((row, idx) => (
                    <tr
                      key={row.component}
                      className={idx % 2 === 0 ? "table-light" : ""}
                    >
                      <td>{row.component}</td>
                      <td>{row.length_1}</td>
                      <td>{row.length_2}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="small text-muted mt-2">
                2mm width reduction considered for each shutter.
              </div>
            </>
          ) : (
            <div className="text-muted">No shutter panels available.</div>
          )}
        </ModalBody>
      </Modal>

      <StoreProjectNameModal
        isOpen={createProjectNameModal.isOpen}
        toggle={() => {
          if (createProjectNameModal.busy) return;
          setCreateProjectNameModal({
            isOpen: false,
            draftName: "",
            busy: false,
            error: null,
          });
        }}
        value={createProjectNameModal.draftName}
        onChange={(value) =>
          setCreateProjectNameModal((m) => ({
            ...m,
            draftName: value,
            error: null,
          }))
        }
        onSubmit={submitCreateProjectNameModal}
        busy={createProjectNameModal.busy}
        error={createProjectNameModal.error}
        submitLabel={
          createProjectNameModal.busy ? "Creating..." : "Create and Add"
        }
      />

      <StoreQuoteFolderPickerModal
        isOpen={folderPickerModal.isOpen}
        toggle={() =>
          setFolderPickerModal({
            isOpen: false,
            projectId: null,
            projectName: "",
          })
        }
        projectId={folderPickerModal.projectId}
        projectName={folderPickerModal.projectName}
        product={pickerProduct}
        onAdded={handleFolderPickerAdded}
        onError={(message) => setAddError(message)}
      />

      <WarrantyTermsModal
        isOpen={isWarrantyTermsOpen}
        terms={productToShow?.warranty?.terms}
        onClose={() => setIsWarrantyTermsOpen(false)}
      />

      <SizingGrid isOpen={isSizingGridOpen} setIsOpen={setIsSizingGridOpen} />

      <SamplePreviewModal
        isOpen={samplePreview.isOpen}
        name={samplePreview.name}
        url={samplePreview.url}
        onClose={() => setSamplePreview({ isOpen: false, name: "", url: "" })}
      />
    </>
  );
};

export default ProductDetailModal;
